import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Rnd } from "react-rnd";
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter } from "@dnd-kit/core";
import CompleteReminderDialog from "@/components/reminders/CompleteReminderDialog";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DigestPreviewModal from "@/components/DigestPreviewModal";
import ReminderEntityPicker from "@/components/reminders/ReminderEntityPicker";
import {
  Clock, X, Plus, Check, AlarmClock, Users, ChevronDown, Trash2,
  Minimize2, Maximize2, Lock, Link2, Repeat, Pencil, MoonStar, LayoutGrid, Mail, GripVertical, Mic,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { fmtRelative } from "@/lib/format";
import { apiErrorMessage } from "@/lib/apiError";

/**
 * Iter 218 — Floating draggable + resizable Reminders panel.
 *
 * Enhancements over Iter 217:
 *  • Linked entity chip (client / brand / project / task / department) shown
 *    with a hyperlink to jump to the item.
 *  • Delegatee name (avatar-initial + label) shown on delegated rows.
 *  • Lock icon on private reminders (no delegate + no entity link).
 *  • Inline "Snoozed until X · Unsnooze" chip.
 *  • Edit dialog (title, note, priority, remind_at, recurrence, delegate, link).
 *  • Recurring (daily/weekly/monthly) in create + edit forms.
 *  • Team View link jumps to /app/reminders/team.
 *  • Visual polish: gradient header, colored priority strip, softer shadow.
 */

const LS_POS_KEY = "co_reminders_panel_pos_v1";
const TABS = [
  { key: "due", label: "Due" },
  { key: "upcoming", label: "Upcoming" },
  { key: "delegated", label: "Delegated" },
  { key: "done", label: "Done" },
];

function loadPos() {
  try {
    const raw = localStorage.getItem(LS_POS_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (typeof p?.x !== "number" || typeof p?.y !== "number") return null;
    return p;
  } catch { return null; }
}
function savePos(p) {
  try { localStorage.setItem(LS_POS_KEY, JSON.stringify(p)); } catch { /* ignore */ }
}
// R268~37 · Feb 2026 — Compute a fresh center-of-screen position for
// the current viewport. Used as the default on every open when the
// user has not explicitly dragged/resized the panel yet.
function centeredPos() {
  const vw = window.innerWidth || 1200;
  const vh = window.innerHeight || 800;
  if (vw < 640) {
    const w = Math.max(300, vw - 12);
    const h = Math.max(400, Math.min(vh - 90, 560));
    return { x: 6, y: 60, w, h };
  }
  const w = 400;
  const h = 560;
  return {
    x: Math.max(16, Math.round((vw - w) / 2)),
    y: Math.max(48, Math.round((vh - h) / 2)),
    w, h,
  };
}
function initialPos() {
  const saved = loadPos();
  // R127 · Feb 2026 — On phones, ignore any saved off-screen position
  // and clamp panel width to the viewport so nothing (including the
  // close/minimize buttons) drifts off-screen.
  // R138f · Feb 2026 — Also clamp on desktop when a saved position was
  // captured on a larger monitor than the current one. Was opening
  // off-screen right when users switched from external → laptop screen.
  const vw = window.innerWidth || 1200;
  const vh = window.innerHeight || 800;
  if (vw < 640) {
    const w = Math.max(300, vw - 12);
    const h = Math.max(400, Math.min(vh - 90, 560));
    return { x: 6, y: 60, w, h };
  }
  const w = 400;
  const h = 560;
  // R268~37 · Feb 2026 — Only honour the saved position when the user
  // has explicitly interacted with the panel (dragged / resized). Fresh
  // opens always re-center so the panel doesn't drift after viewport
  // changes.
  const defaultX = Math.max(16, Math.round((vw - w) / 2));
  const defaultY = Math.max(48, Math.round((vh - h) / 2));
  if (saved && saved.moved) {
    const sw = Math.min(saved.w || w, vw - 24);
    const sh = Math.min(saved.h || h, vh - 24);
    const wouldOverflowX = (saved.x ?? 0) + sw + 8 > vw;
    const wouldOverflowY = (saved.y ?? 0) + sh + 8 > vh;
    const sx = wouldOverflowX
      ? Math.max(8, vw - sw - 8)
      : Math.max(8, Math.min(saved.x ?? defaultX, vw - sw - 8));
    const sy = wouldOverflowY
      ? Math.max(8, vh - sh - 8)
      : Math.max(8, Math.min(saved.y ?? defaultY, vh - sh - 8));
    return { x: sx, y: sy, w: sw, h: sh, moved: true };
  }
  return { x: defaultX, y: defaultY, w, h, moved: false };
}

const PRIORITY_STRIP = {
  low: "bg-slate-400",
  normal: "bg-sky-500",
  high: "bg-amber-500",
  urgent: "bg-rose-500",
};
const PRIORITY_CHIP = {
  low: "bg-slate-100 text-slate-700 border-slate-300",
  normal: "bg-sky-50 text-sky-700 border-sky-300",
  high: "bg-amber-50 text-amber-700 border-amber-300",
  urgent: "bg-rose-50 text-rose-700 border-rose-300",
};

function Initials({ name, avatar, size = 5 }) {
  const cls = `w-${size} h-${size} rounded-full bg-primary/15 text-primary text-[9px] font-semibold flex items-center justify-center shrink-0 overflow-hidden`;
  if (avatar) return <img src={avatar} alt={name || ""} className={cls} />;
  const initials = (name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  return <span className={cls}>{initials}</span>;
}

export default function RemindersPanel({ open, onClose }) {
  const { activeWorkspaceId, user } = useAuth();
  const [pos, setPos] = useState(initialPos);
  const [minimized, setMinimized] = useState(false);
  const [tab, setTab] = useState("due");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [digestPreviewOpen, setDigestPreviewOpen] = useState(false);   // Iter 231
  // Iter 253o · Tab-count badges + bulk-archive.
  const [tabCounts, setTabCounts] = useState({ due: 0, upcoming: 0, delegated: 0, done: 0 });
  const [archiving, setArchiving] = useState(false);
  // R268~36 · Feb 2026 — Live quick-search bar. Client-side only, so
  // matches happen the moment the user types. Reset when the popup
  // reopens on the "due" tab so a stale search doesn't hide items on
  // a fresh open.
  const [query, setQuery] = useState("");
  useEffect(() => { if (open) setQuery(""); }, [open]);
  // R268~28 · Feb 2026 — Sort options for the visible list. "manual"
  // preserves the user's drag-persisted order (default) — every other
  // value overrides it for a quick scan by priority / due / age.
  const REM_PRIORITY_ORDER = { urgent: 0, high: 1, normal: 2, medium: 2, low: 3 };
  const [sortBy, setSortBy] = useState(() => {
    try { return localStorage.getItem("co_reminders_panel_sort") || "manual"; } catch { return "manual"; }
  });
  useEffect(() => {
    try { localStorage.setItem("co_reminders_panel_sort", sortBy); } catch {}
  }, [sortBy]);

  // Feb 2026 · Due-date bucket filter — chips above the list.
  // "all" | "overdue" | "today" | "week" | "month"
  const [dueBucket, setDueBucket] = useState("all");

  // R160g · Feb 2026 — Drag-to-reorder inside the header Reminders bell.
  // Reuses the same POST /reminders/reorder endpoint the Team Reminders
  // pinboard hits so the custom order surfaces everywhere.
  const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const persistReminderOrder = async (ids) => {
    if (!activeWorkspaceId) return;
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/reminders/reorder`, { ids });
    } catch (e) { toast.error(apiErrorMessage(e, "Reorder failed")); }
  };
  const handleReorder = (event) => {
    const { active, over } = event || {};
    if (!over || active.id === over.id) return;
    // Reorder within the currently-filtered view; then splice the moved
    // ids back into the full `rows` array so tabs that share filtered
    // slices stay consistent.
    const visibleIds = filteredRows.map((r) => r.id);
    const from = visibleIds.indexOf(active.id);
    const to = visibleIds.indexOf(over.id);
    if (from === -1 || to === -1) return;
    const nextVisibleIds = arrayMove(visibleIds, from, to);
    const visibleSet = new Set(visibleIds);
    const nextRows = [...rows];
    let cursor = 0;
    for (let i = 0; i < nextRows.length; i++) {
      if (visibleSet.has(nextRows[i].id)) {
        const nextId = nextVisibleIds[cursor++];
        nextRows[i] = rows.find((r) => r.id === nextId);
      }
    }
    setRows(nextRows);
    persistReminderOrder(nextRows.map((r) => r.id));
  };

  // Iter 231 · Auto-open digest preview when landing here via
  // /app/team-reminders?preview=1 (from the Sunday-evening notification).
  useEffect(() => {
    if (!open) return;
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("preview") === "1") setDigestPreviewOpen(true);
    } catch { /* noop */ }
  }, [open]);

  // Quick add
  const [quickTitle, setQuickTitle] = useState("");
  const [quickNote, setQuickNote] = useState("");
  const [quickPriority, setQuickPriority] = useState("normal");
  const [quickRemindAt, setQuickRemindAt] = useState("");
  const [quickRecurrence, setQuickRecurrence] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [members, setMembers] = useState([]);
  const [delegatedTo, setDelegatedTo] = useState("");
  const [clientId, setClientId] = useState("");
  const [brandId, setBrandId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [taskId, setTaskId] = useState("");
  // Options for the searchable pickers — lazy-loaded on advanced-panel open.
  const [clientOpts, setClientOpts] = useState([]);
  const [brandOpts, setBrandOpts] = useState([]);
  const [projectOpts, setProjectOpts] = useState([]);
  const [taskOpts, setTaskOpts] = useState([]);
  const [quickIsPrivate, setQuickIsPrivate] = useState(false);   // Iter 229
  const inputRef = useRef(null);

  // Edit modal
  const [editing, setEditing] = useState(null); // reminder row or null

  useEffect(() => {
    // R268~37 · Feb 2026 — Recompute the position every time the panel
    // opens. If the user has previously dragged/resized, initialPos()
    // will return the saved location; otherwise it re-centers on the
    // current viewport so the panel doesn't drift after resizes.
    if (open) setPos(initialPos());
  }, [open]);

  useEffect(() => {
    if (!open || !activeWorkspaceId) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeWorkspaceId, tab]);

  useEffect(() => {
    if (!open || !activeWorkspaceId || members.length) return;
    api.get(`/workspaces/${activeWorkspaceId}/members`)
      .then(({ data }) => setMembers(Array.isArray(data) ? data : (data.members || [])))
      .catch(() => {});
  }, [open, activeWorkspaceId, members.length]);

  useEffect(() => {
    if (open && !minimized) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open, minimized]);

  const load = async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try {
      let params = { scope: "visible" };
      if (tab === "delegated") params = { scope: "delegated", status: "open" };
      else if (tab === "done") params = { scope: "visible", status: "done" };
      else params = { scope: "visible", status: "open" };
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/reminders`, { params });
      setRows(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Failed to load reminders");
    } finally {
      setLoading(false);
    }
    // Iter 253o · Refresh tab-count badges alongside the list so numbers
    // stay in sync after each action (create / complete / archive).
    try {
      const { data: counts } = await api.get(
        `/workspaces/${activeWorkspaceId}/reminders/tab-counts`);
      setTabCounts(counts || {});
    } catch { /* ignore */ }
    try { window.dispatchEvent(new CustomEvent("reminders:changed")); } catch { /* ignore */ }
  };

  // Iter 253o · Bulk-archive helper for the Done tab.
  const archiveAllDone = async () => {
    const doneRows = rows.filter((r) => r.status === "done" && r.owner_id === user?.id);
    if (doneRows.length === 0) return;
    if (!window.confirm(`Archive ${doneRows.length} done reminder${doneRows.length === 1 ? "" : "s"}? They'll be hidden but not permanently deleted.`)) return;
    setArchiving(true);
    try {
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/reminders/bulk-archive`,
        { ids: doneRows.map((r) => r.id) });
      toast.success(`Archived ${data.archived} reminder${data.archived === 1 ? "" : "s"}.`);
      load();
    } catch (e) {
      toast.error("Bulk archive failed");
    } finally { setArchiving(false); }
  };

  // Load client / brand / project / task options once the workspace is
  // known so all picker dropdowns are ready by the time the user opens
  // the advanced panel. Task list is capped to the workspace's 500 most
  // recent to keep the payload small — combobox has a searchbox anyway.
  useEffect(() => {
    if (!open || !activeWorkspaceId) return;
    let cancelled = false;
    (async () => {
      const ws = activeWorkspaceId;
      const [cs, bs, ps, ts] = await Promise.allSettled([
        api.get(`/workspaces/${ws}/clients`),
        api.get(`/workspaces/${ws}/brands`),
        api.get(`/workspaces/${ws}/projects`),
        api.get(`/workspaces/${ws}/tasks-search`, { params: { q: "", limit: 500 } }),
      ]);
      if (cancelled) return;
      const toRows = (r) => {
        if (r.status !== "fulfilled") return [];
        const d = r.value?.data;
        return Array.isArray(d) ? d : (d?.items || d?.rows || []);
      };
      setClientOpts(toRows(cs).map((r) => ({ id: r.id, label: r.name || r.title || r.id })));
      setBrandOpts(toRows(bs).map((r) => ({ id: r.id, label: r.name || r.id, client_id: r.client_id || null })));
      setProjectOpts(toRows(ps).map((r) => ({ id: r.id, label: r.name || r.id, sublabel: r.client_name, client_id: r.client_id || null, brand_id: r.brand_id || null })));
      setTaskOpts(toRows(ts).map((r) => ({ id: r.id, label: r.title || r.id, sublabel: r.project_name, project_id: r.project_id || null, client_id: r.client_id || null, brand_id: r.brand_id || null })));
    })();
    return () => { cancelled = true; };
  }, [open, activeWorkspaceId]);

  // Feb 2026 · Reminders Overhaul — Cascade filters: Brand depends on
  // Client; Project depends on Client + Brand; Task depends on Project
  // (and by extension, its Client/Brand). Strict filtering — once a
  // parent is picked, we only show children explicitly attached to it.
  const brandChoices = useMemo(() => {
    if (!clientId) return brandOpts;
    return brandOpts.filter((b) => b.client_id === clientId);
  }, [brandOpts, clientId]);
  const projectChoices = useMemo(() => {
    return projectOpts.filter((p) => {
      if (clientId && p.client_id !== clientId) return false;
      if (brandId && p.brand_id !== brandId) return false;
      return true;
    });
  }, [projectOpts, clientId, brandId]);
  const taskChoices = useMemo(() => {
    return taskOpts.filter((t) => {
      if (projectId) return t.project_id === projectId;
      if (clientId && t.client_id !== clientId) return false;
      if (brandId && t.brand_id !== brandId) return false;
      return true;
    });
  }, [taskOpts, projectId, clientId, brandId]);

  // Auto-clear stale child selections when the parent narrows them out.
  useEffect(() => {
    if (brandId && !brandChoices.some((b) => b.id === brandId)) setBrandId("");
  }, [brandId, brandChoices]);
  useEffect(() => {
    if (projectId && !projectChoices.some((p) => p.id === projectId)) setProjectId("");
  }, [projectId, projectChoices]);
  useEffect(() => {
    if (taskId && !taskChoices.some((t) => t.id === taskId)) setTaskId("");
  }, [taskId, taskChoices]);

  const filteredRows = useMemo(() => {
    const now = new Date().toISOString();
    let out;
    if (tab === "due") {
      out = rows.filter((r) => {
        if (r.status !== "open") return false;
        const notSnoozed = !r.snooze_until || r.snooze_until <= now;
        const fired = !r.remind_at || r.remind_at <= now;
        return notSnoozed && fired;
      });
    } else if (tab === "upcoming") {
      out = rows.filter((r) => r.status === "open" && (
        (r.remind_at && r.remind_at > now) || (r.snooze_until && r.snooze_until > now)
      ));
    } else {
      out = rows;
    }
    // R268~36 · Feb 2026 — Client-side quick-search across every human
    // field on the reminder so managers can find anything by typing.
    const q = query.trim().toLowerCase();
    if (q) {
      const hit = (v) => v != null && String(v).toLowerCase().includes(q);
      out = out.filter((r) => (
        hit(r.title) || hit(r.note) || hit(r.response_note) ||
        hit(r.priority) ||
        hit(r.owner_name) || hit(r.delegated_to_name) || hit(r.closed_by_name) ||
        hit(r.client_name) || hit(r.brand_name) || hit(r.project_name) || hit(r.task_title)
      ));
    }
    // Feb 2026 · Due-date bucket filter — overlays on top of the tab.
    if (dueBucket !== "all") {
      const nowD = new Date();
      const startOfToday = new Date(nowD.getFullYear(), nowD.getMonth(), nowD.getDate()).getTime();
      const endOfToday = startOfToday + 86_400_000;
      const endOfWeek = startOfToday + 7 * 86_400_000;
      const endOfMonth = startOfToday + 31 * 86_400_000;
      const in24h = nowD.getTime() + 24 * 60 * 60 * 1000;
      out = out.filter((r) => {
        if (!r.remind_at) return false;
        const t = new Date(r.remind_at).getTime();
        if (!Number.isFinite(t)) return false;
        if (dueBucket === "overdue") return t < nowD.getTime() && r.status === "open";
        if (dueBucket === "next24h") return t >= nowD.getTime() && t <= in24h;
        if (dueBucket === "today")   return t >= startOfToday && t < endOfToday;
        if (dueBucket === "week")    return t >= startOfToday && t < endOfWeek;
        if (dueBucket === "month")   return t >= startOfToday && t < endOfMonth;
        return true;
      });
    }
    // R268~28 · Feb 2026 — Apply sortBy on top of the tab filter. Return
    // a new array so React sees the change; "manual" leaves the array
    // as-is (server order + user drag).
    if (sortBy === "manual") return out;
    const sorted = [...out];
    sorted.sort((x, y) => {
      if (sortBy === "priority") {
        return (REM_PRIORITY_ORDER[x.priority] ?? 9) - (REM_PRIORITY_ORDER[y.priority] ?? 9);
      }
      if (sortBy === "due_soon" || sortBy === "due_late") {
        const wx = x.remind_at || x.due_at || "";
        const wy = y.remind_at || y.due_at || "";
        if (wx && !wy) return -1;
        if (!wx && wy) return 1;
        if (wx === wy) return 0;
        return sortBy === "due_soon" ? wx.localeCompare(wy) : wy.localeCompare(wx);
      }
      if (sortBy === "newest" || sortBy === "oldest") {
        const cx = x.created_at || x.remind_at || "";
        const cy = y.created_at || y.remind_at || "";
        if (cx === cy) return 0;
        return sortBy === "newest" ? cy.localeCompare(cx) : cx.localeCompare(cy);
      }
      if (sortBy === "title") return (x.title || "").localeCompare(y.title || "");
      return 0;
    });
    return sorted;
  }, [rows, tab, sortBy, dueBucket, query]);

  // Feb 2026 · Reminders Overhaul — only reset the title/note. Every
  // other selection (delegatee, client, brand, project, task, remind_at,
  // priority, recurrence, privacy) is retained so users can fire off
  // multiple reminders for the same brand/client without re-picking.
  // Full reset happens via the "Clear scope" button in the advanced
  // panel.
  const resetQuick = () => {
    setQuickTitle(""); setQuickNote("");
  };
  const clearScope = () => {
    setDelegatedTo(""); setClientId(""); setBrandId("");
    setProjectId(""); setTaskId(""); setQuickRemindAt("");
    setQuickRecurrence(""); setQuickPriority("normal");
    setQuickIsPrivate(false);
  };

  const submitQuick = async () => {
    if (!quickTitle.trim()) { toast.error("Add a title"); return; }
    try {
      const payload = {
        title: quickTitle.trim(),
        priority: quickPriority,
      };
      if (quickNote.trim()) payload.note = quickNote.trim();
      if (quickRemindAt) payload.remind_at = new Date(quickRemindAt).toISOString();
      if (quickRecurrence) payload.recurrence = quickRecurrence;
      if (delegatedTo) payload.delegated_to = delegatedTo;
      if (clientId) payload.client_id = clientId;
      if (brandId) payload.brand_id = brandId;
      if (projectId) payload.project_id = projectId;
      if (taskId) payload.task_id = taskId;
      if (quickIsPrivate) payload.is_private = true;
      await api.post(`/workspaces/${activeWorkspaceId}/reminders`, payload);
      resetQuick();
      toast.success("Reminder added");
      // Refocus the title so the next reminder for the same scope is
      // a single keystroke away.
      setTimeout(() => inputRef.current?.focus(), 30);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to add"));
    }
  };

  // R268~3 · Feb 2026 — Marking done now opens a dialog that captures
  // a mandatory response note before hitting the complete endpoint.
  // The dialog owns the POST + toast; the panel only reloads.
  const [completing, setCompleting] = useState(null);
  const complete = (r) => setCompleting(r);
  const snoozeQuick = async (r, mins) => {
    const until = new Date(Date.now() + mins * 60 * 1000).toISOString();
    try { await api.post(`/reminders/${r.id}/snooze`, { snooze_until: until }); toast.success("Snoozed"); load(); }
    catch { toast.error("Snooze failed"); }
  };
  const unsnooze = async (r) => {
    try { await api.post(`/reminders/${r.id}/unsnooze`); toast.success("Unsnoozed"); load(); }
    catch { toast.error("Unsnooze failed"); }
  };
  const remove = async (r) => {
    if (!window.confirm("Delete this reminder?")) return;
    try { await api.delete(`/reminders/${r.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  if (!open) return null;

  return (
    <>
      <Rnd
        size={{ width: pos.w, height: minimized ? 44 : pos.h }}
        position={{ x: pos.x, y: pos.y }}
        minWidth={340}
        minHeight={minimized ? 44 : 340}
        maxWidth={780}
        maxHeight={900}
        bounds="window"
        dragHandleClassName="reminders-panel-drag-handle"
        // R127 · Feb 2026 — Exclude every button in the header from the
        // drag handle so tap-to-close / tap-to-minimize works on mobile
        // (react-rnd was swallowing touch events, leaving buttons dead).
        cancel=".rnd-cancel"
        onDragStop={(_, d) => {
          const next = { ...pos, x: d.x, y: d.y, moved: true };
          setPos(next);
          savePos(next);
        }}
        onResizeStop={(_, __, ref, ___, position) => {
          const next = {
            x: position.x, y: position.y,
            w: parseInt(ref.style.width, 10),
            h: minimized ? pos.h : parseInt(ref.style.height, 10),
            moved: true,
          };
          setPos(next);
          savePos(next);
        }}
        style={{ zIndex: 1000 }}
        enableResizing={!minimized}
      >
        <div
          className="h-full w-full flex flex-col border border-border/80 bg-background overflow-hidden"
          style={{ boxShadow: "0 24px 60px -12px rgba(0,0,0,0.35), 0 8px 16px -4px rgba(0,0,0,0.15)" }}
          data-testid="reminders-panel"
        >
          {/* Header (drag handle) — gradient */}
          <div
            className="reminders-panel-drag-handle cursor-move flex items-center justify-between px-3 py-2 border-b border-border select-none"
            style={{
              background: "linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--primary) / 0.85) 40%, hsl(var(--primary) / 0.65) 100%)",
              color: "hsl(var(--primary-foreground))",
            }}
          >
            <div className="flex items-center gap-2 text-xs font-semibold tracking-wide">
              <AlarmClock className="w-3.5 h-3.5" />
              <span>Reminders</span>
              <span className="text-[10px] opacity-80 font-normal">
                {rows.length} {tab === "done" ? "completed" : "open"}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setDigestPreviewOpen(true)}
                className="rnd-cancel p-2 -m-1 opacity-80 hover:opacity-100 min-h-[36px] min-w-[36px] flex items-center justify-center"
                title="Preview tomorrow's digest email"
                data-testid="reminders-digest-preview-btn"
              >
                <Mail className="w-4 h-4" />
              </button>
              <Link
                to="/app/reminders/team"
                onClick={onClose}
                className="rnd-cancel p-2 -m-1 opacity-80 hover:opacity-100 min-h-[36px] min-w-[36px] flex items-center justify-center"
                title="Team view"
                data-testid="reminders-team-link"
              >
                <LayoutGrid className="w-4 h-4" />
              </Link>
              <button
                type="button"
                onClick={() => setMinimized((m) => !m)}
                className="rnd-cancel p-2 -m-1 opacity-80 hover:opacity-100 min-h-[36px] min-w-[36px] flex items-center justify-center"
                data-testid="reminders-minimize-btn"
                aria-label={minimized ? "Expand" : "Minimize"}
              >
                {minimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="rnd-cancel p-2 -m-1 opacity-80 hover:opacity-100 min-h-[36px] min-w-[36px] flex items-center justify-center"
                data-testid="reminders-close-btn"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {!minimized && (
            <>
              {/* Tabs */}
              <div className="flex border-b border-border text-xs bg-muted/30">
                {TABS.map((t) => {
                  const cnt = tabCounts[t.key] || 0;
                  const showCnt = cnt > 0;
                  const emphasize = t.key === "due" && cnt > 0;
                  return (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => setTab(t.key)}
                      className={`flex-1 px-2 py-2 transition font-medium flex items-center justify-center gap-1 ${tab === t.key ? "bg-background border-b-2 border-primary text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                      data-testid={`reminders-tab-${t.key}`}
                    >
                      <span>{t.label}</span>
                      {showCnt && (
                        <span
                          className={`inline-flex items-center justify-center min-w-[16px] h-4 px-1 text-[9px] font-semibold rounded ${emphasize ? "bg-rose-500 text-white" : (tab === t.key ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground")}`}
                          data-testid={`reminders-tab-count-${t.key}`}
                        >
                          {cnt > 99 ? "99+" : cnt}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Iter 253o · Done-tab utility bar for bulk archive. */}
              {tab === "done" && rows.length > 0 && (
                <div className="border-b border-border px-2.5 py-1.5 flex items-center justify-between gap-2 bg-muted/20 text-[10px]">
                  <span className="text-muted-foreground">
                    Done reminders stay here until you archive them.
                  </span>
                  <button
                    type="button"
                    onClick={archiveAllDone}
                    disabled={archiving}
                    className="px-2 py-1 text-[10px] border border-border hover:bg-muted flex items-center gap-1 disabled:opacity-50"
                    data-testid="reminders-archive-all-done"
                    title="Archive every done reminder you own (soft delete — recoverable by admins)"
                  >
                    <Trash2 className="w-3 h-3" /> {archiving ? "Archiving…" : "Archive all done"}
                  </button>
                </div>
              )}

              {/* Quick add */}
              <div className="border-b border-border p-2.5 space-y-2 bg-gradient-to-b from-muted/40 to-transparent">
                <div className="flex items-center gap-1.5">
                  <input
                    ref={inputRef}
                    value={quickTitle}
                    onChange={(e) => setQuickTitle(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") submitQuick(); }}
                    placeholder="Remind me to…"
                    className="flex-1 text-xs px-2.5 py-2 bg-background border border-border focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/30"
                    data-testid="reminders-quick-input"
                  />
                  <VoiceDictateButton
                    onParsed={({ title, remindAt }) => {
                      if (title) setQuickTitle((prev) => (prev ? `${prev.trim()} ${title}` : title));
                      if (remindAt) {
                        // toLocal formatter for the datetime-local input
                        const pad = (n) => String(n).padStart(2, "0");
                        const d = new Date(remindAt);
                        const local = `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
                        setQuickRemindAt(local);
                        try { toast.success(`Due date set · ${d.toLocaleString()}`); } catch {}
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={submitQuick}
                    className="text-xs px-3 py-2 bg-primary text-primary-foreground hover:opacity-90 font-medium flex items-center gap-1"
                    data-testid="reminders-quick-submit"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add
                  </button>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <PriorityPicker value={quickPriority} onChange={setQuickPriority} />
                  <input
                    type="datetime-local"
                    value={quickRemindAt}
                    onChange={(e) => setQuickRemindAt(e.target.value)}
                    className="text-[11px] px-2 py-1 bg-background border border-border"
                    data-testid="reminders-remind-at"
                    title="Remind me at"
                  />
                  <select
                    value={quickRecurrence}
                    onChange={(e) => setQuickRecurrence(e.target.value)}
                    className="text-[11px] px-1.5 py-1 bg-background border border-border"
                    data-testid="reminders-recurrence"
                    title="Repeat"
                  >
                    <option value="">Once</option>
                    <optgroup label="Simple">
                      <option value="daily">Every day</option>
                      <option value="weekdays">Every weekday (Mon–Fri)</option>
                      <option value="weekly">Every week</option>
                      <option value="biweekly">Every 2 weeks</option>
                      <option value="monthly">Every month</option>
                      <option value="quarterly">Every 3 months</option>
                      <option value="yearly">Every year</option>
                    </optgroup>
                    <optgroup label="Specific weekday">
                      <option value="weekly_mon">Every Monday</option>
                      <option value="weekly_tue">Every Tuesday</option>
                      <option value="weekly_wed">Every Wednesday</option>
                      <option value="weekly_thu">Every Thursday</option>
                      <option value="weekly_fri">Every Friday</option>
                      <option value="weekly_sat">Every Saturday</option>
                      <option value="weekly_sun">Every Sunday</option>
                    </optgroup>
                    <optgroup label="Monthly patterns">
                      <option value="monthly_first">First of the month</option>
                      <option value="monthly_last">Last of the month</option>
                      <option value="monthly_15">Mid-month (15th)</option>
                    </optgroup>
                  </select>
                  <button
                    type="button"
                    onClick={() => setQuickIsPrivate((v) => !v)}
                    className={`text-[11px] px-2 py-1 border inline-flex items-center gap-1 transition font-semibold ${quickIsPrivate ? "bg-amber-500 text-white border-amber-600 shadow-sm" : "bg-background text-muted-foreground border-border hover:border-amber-400"}`}
                    data-testid="reminders-quick-private-toggle"
                    aria-pressed={quickIsPrivate}
                    title={quickIsPrivate ? "PRIVATE — only you will see this reminder. Click to make it visible to your team." : "Currently visible to your team via project/brand/client/department links. Click to make it Only me."}
                  >
                    <Lock className="w-2.5 h-2.5" />
                    {quickIsPrivate ? "ONLY ME" : "Team-visible"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAdvanced((s) => !s)}
                    className="text-[11px] text-muted-foreground hover:text-foreground flex items-center gap-1 ml-auto"
                    data-testid="reminders-advanced-toggle"
                  >
                    <ChevronDown className={`w-3 h-3 transition ${showAdvanced ? "rotate-180" : ""}`} />
                    {showAdvanced ? "Less" : "Scope · delegate · link"}
                  </button>
                </div>
                {showAdvanced && (
                  <div className="pt-2 space-y-2 border-t border-dashed border-border/60">
                    {/* Optional note */}
                    <textarea
                      value={quickNote}
                      onChange={(e) => setQuickNote(e.target.value)}
                      placeholder="Optional note or context…"
                      rows={2}
                      className="w-full text-[11px] px-2 py-1.5 bg-background border border-border focus:outline-none focus:border-primary resize-y"
                      data-testid="reminders-quick-note"
                    />
                    <div className="grid grid-cols-1 gap-1.5">
                      <label className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">Delegate to</label>
                      <ReminderEntityPicker
                        value={delegatedTo}
                        options={members
                          .filter((m) => (m.user_id || m.id) !== user?.id)
                          .map((m) => ({
                            id: m.user_id || m.id,
                            label: (m.user?.name || m.name || m.user?.email || m.email || "").trim(),
                            sublabel: (m.user?.email || m.email) && (m.user?.name || m.name) ? (m.user?.email || m.email) : null,
                          }))
                          .filter((m) => m.label)}
                        onChange={setDelegatedTo}
                        placeholder="— Delegate to teammate —"
                        emptyMsg="No teammates."
                        testid="reminders-delegate-select"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-1.5">
                      <div>
                        <label className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">Client</label>
                        <ReminderEntityPicker
                          value={clientId}
                          options={clientOpts}
                          onChange={setClientId}
                          placeholder="— Client —"
                          testid="reminders-link-client"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">
                          Brand {clientId && <span className="text-[9px] text-muted-foreground/80 lowercase">· filtered</span>}
                        </label>
                        <ReminderEntityPicker
                          value={brandId}
                          options={brandChoices}
                          onChange={setBrandId}
                          placeholder={clientId && brandChoices.length === 0 ? "No brands for this client" : "— Brand —"}
                          testid="reminders-link-brand"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">
                          Project {(clientId || brandId) && <span className="text-[9px] text-muted-foreground/80 lowercase">· filtered</span>}
                        </label>
                        <ReminderEntityPicker
                          value={projectId}
                          options={projectChoices}
                          onChange={setProjectId}
                          placeholder={(clientId || brandId) && projectChoices.length === 0 ? "No projects match" : "— Project —"}
                          testid="reminders-link-project"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">
                          Task {(projectId || clientId || brandId) && <span className="text-[9px] text-muted-foreground/80 lowercase">· filtered</span>}
                        </label>
                        <ReminderEntityPicker
                          value={taskId}
                          options={taskChoices}
                          onChange={setTaskId}
                          placeholder={(projectId || clientId || brandId) && taskChoices.length === 0 ? "No tasks match" : "— Task —"}
                          testid="reminders-link-task"
                        />
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-2 pt-1">
                      <span className="text-[10px] text-muted-foreground">
                        Selections stay after Add — fire many for the same scope.
                      </span>
                      <button
                        type="button"
                        onClick={clearScope}
                        className="text-[10px] text-muted-foreground hover:text-rose-600 underline"
                        data-testid="reminders-clear-scope"
                      >
                        Clear scope
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* List */}
              <div className="flex-1 overflow-y-auto" data-testid="reminders-list">
                {/* R268~28 · Feb 2026 — Sort dropdown for the current
                    tab's list. Sticky so it stays visible while
                    scrolling through many rows. */}
                {/* Feb 2026 · Due-date bucket chips */}
                <div className="sticky top-0 z-10 flex items-center gap-1 border-b border-border bg-background px-2.5 py-1.5 overflow-x-auto" data-testid="reminders-due-filters">
                  {[
                    { k: "all", label: "All" },
                    { k: "overdue", label: "Overdue" },
                    { k: "next24h", label: "Next 24h" },
                    { k: "today", label: "Today" },
                    { k: "week", label: "This week" },
                    { k: "month", label: "This month" },
                  ].map((b) => {
                    const active = dueBucket === b.k;
                    const rose = b.k === "overdue";
                    return (
                      <button key={b.k}
                              type="button"
                              onClick={() => setDueBucket(b.k)}
                              className={`px-2 py-1 text-[10px] uppercase tracking-widest font-semibold border whitespace-nowrap transition-colors ${
                                active
                                  ? (rose
                                      ? "bg-rose-500 text-white border-rose-600"
                                      : "bg-primary text-primary-foreground border-primary")
                                  : (rose
                                      ? "bg-background border-rose-200 text-rose-600 hover:border-rose-400"
                                      : "bg-background border-border text-muted-foreground hover:border-primary hover:text-primary")
                              }`}
                              data-testid={`reminders-due-chip-${b.k}`}>
                        {b.label}
                      </button>
                    );
                  })}
                </div>
                <div className="sticky top-[36px] z-10 flex items-center gap-2 border-b border-border bg-muted/40 px-2.5 py-1.5 text-[10px]">
                  <span className="uppercase tracking-widest text-muted-foreground">Sort</span>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="text-[10px] px-1.5 py-0.5 bg-background border border-border focus:outline-none focus:border-primary"
                    data-testid="reminders-panel-sortby"
                    title="Sort visible reminders"
                  >
                    <option value="manual">Manual (drag)</option>
                    <option value="priority">Priority · high → low</option>
                    <option value="due_soon">Due · soonest</option>
                    <option value="due_late">Due · latest</option>
                    <option value="newest">Newest first</option>
                    <option value="oldest">Oldest first</option>
                    <option value="title">Title A → Z</option>
                  </select>
                  <span className="ml-auto text-muted-foreground font-mono">
                    {filteredRows.length} {filteredRows.length === 1 ? "item" : "items"}
                  </span>
                </div>
                {loading && (
                  <div className="p-4 text-xs text-muted-foreground text-center">Loading…</div>
                )}
                {!loading && filteredRows.length === 0 && (
                  <div className="p-8 text-xs text-muted-foreground text-center">
                    <AlarmClock className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    Nothing here. Add a reminder above.
                  </div>
                )}
                {filteredRows.length > 0 && (
                  <DndContext
                    sensors={dndSensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleReorder}
                  >
                    <SortableContext
                      items={filteredRows.map((r) => r.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {filteredRows.map((r, idx) => (
                        <SortableReminderRow
                          key={r.id}
                          r={r}
                          rowIndex={idx + 1}
                          currentUserId={user?.id}
                          onComplete={() => complete(r)}
                          onSnooze={(mins) => snoozeQuick(r, mins)}
                          onUnsnooze={() => unsnooze(r)}
                          onDelete={() => remove(r)}
                          onEdit={() => setEditing(r)}
                          onCloseFloating={onClose}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                )}
              </div>
            </>
          )}
        </div>
      </Rnd>

      <DigestPreviewModal
        open={digestPreviewOpen}
        onClose={() => setDigestPreviewOpen(false)}
      />

      {editing && (
        <EditReminderDialog
          reminder={editing}
          members={members}
          currentUserId={user?.id}
          workspaceId={activeWorkspaceId}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {completing && (
        <CompleteReminderDialog
          reminder={completing}
          onClose={() => setCompleting(null)}
          onDone={() => { setCompleting(null); load(); }}
        />
      )}
    </>
  );
}

function PriorityPicker({ value, onChange }) {
  const opts = [
    { v: "low", label: "Low" },
    { v: "normal", label: "Normal" },
    { v: "high", label: "High" },
    { v: "urgent", label: "Urgent" },
  ];
  return (
    <div className="inline-flex items-center border border-border overflow-hidden text-[10px]" data-testid="reminders-quick-priority">
      {opts.map((o) => (
        <button
          key={o.v}
          type="button"
          onClick={() => onChange(o.v)}
          className={`px-1.5 py-1 transition ${value === o.v ? PRIORITY_CHIP[o.v] + " border-l border-border first:border-l-0" : "text-muted-foreground hover:text-foreground border-l border-border first:border-l-0"}`}
          data-testid={`reminders-priority-${o.v}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function SortableReminderRow(props) {
  // R160g · Feb 2026 — Drag-to-reorder wrapper around ReminderRow. The
  // ReminderRow itself stays purely presentational; the handle is
  // injected via `dragHandleProps` so the row author never has to think
  // about DND concerns.
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: props.r.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 10 : "auto",
  };
  return (
    <div ref={setNodeRef} style={style} data-testid={`bell-sortable-reminder-${props.r.id}`}>
      <ReminderRow {...props} dragHandleProps={{ ...attributes, ...listeners }} />
    </div>
  );
}

function ReminderRow({ r, currentUserId, onComplete, onSnooze, onUnsnooze, onDelete, onEdit, onCloseFloating, dragHandleProps }) {
  const isDelegatee = r.delegated_to === currentUserId && r.owner_id !== currentUserId;
  const done = r.status === "done";
  const now = new Date().toISOString();
  const isSnoozed = !!r.snooze_until && r.snooze_until > now;
  const isOverdue = !done && !isSnoozed && r.remind_at && r.remind_at <= now;
  const priStrip = PRIORITY_STRIP[r.priority] || PRIORITY_STRIP.normal;
  const priChip = PRIORITY_CHIP[r.priority] || PRIORITY_CHIP.normal;
  const isPrivate = !!r.is_private;
  const showCreator = !!r.owner_id && r.owner_id !== currentUserId;

  // Feb 2026 · Absolute + relative due date. `fmtRelative` gives us
  // "in 2h", "3d ago" style; we also render an absolute short date so
  // users don't have to hover to know exactly when.
  const dueAbsolute = r.remind_at ? _fmtAbsShort(r.remind_at) : null;
  const dueRelative = r.remind_at ? fmtRelative(r.remind_at) : null;

  return (
    <div
      className={`relative pl-3 pr-2 py-2.5 border-b hover:bg-muted/30 group ${done ? "opacity-70" : ""} ${isPrivate ? "bg-amber-50/60 dark:bg-amber-950/20 border-amber-300/70 dark:border-amber-700/50 border-l-2 border-l-amber-500" : "border-border"}`}
      data-testid={`reminder-row-${r.id}`}
      data-private={isPrivate ? "true" : "false"}
    >
      {!isPrivate && <div className={`absolute left-0 top-0 bottom-0 w-1 ${priStrip}`} />}

      {/* Row 1 — Handle · Checkbox · Title (full width) · Age */}
      <div className="flex items-start gap-2">
        {dragHandleProps && (
          <button
            type="button"
            {...dragHandleProps}
            className="mt-0.5 -ml-1 p-0.5 text-muted-foreground/50 hover:text-muted-foreground cursor-grab active:cursor-grabbing touch-none shrink-0"
            aria-label="Drag to reorder"
            data-testid={`bell-drag-handle-${r.id}`}
            title="Drag to reorder"
          >
            <GripVertical className="w-3 h-3" />
          </button>
        )}
        <button
          type="button"
          onClick={onComplete}
          disabled={done}
          className={`mt-0.5 w-4 h-4 rounded-full border ${done ? "bg-primary border-primary" : "border-muted-foreground hover:border-primary hover:bg-primary/10"} flex items-center justify-center shrink-0 transition`}
          data-testid={`reminder-complete-${r.id}`}
          aria-label="Mark done"
        >
          {done && <Check className="w-2.5 h-2.5 text-primary-foreground" />}
        </button>
        <div className={`text-[13px] font-semibold leading-snug break-words flex-1 min-w-0 ${done ? "line-through text-muted-foreground" : "text-foreground"}`}
             data-testid={`reminder-title-${r.id}`}>
          {r.title}
        </div>
        {(() => {
          const created = r.created_at;
          if (!created) return null;
          const then = new Date(created).getTime();
          if (!Number.isFinite(then)) return null;
          const diffMs = Date.now() - then;
          let txt;
          if (diffMs < 60_000) txt = "just now";
          else if (diffMs < 3_600_000) txt = `${Math.round(diffMs / 60_000)}m`;
          else if (diffMs < 86_400_000) txt = `${Math.round(diffMs / 3_600_000)}h`;
          else if (diffMs < 30 * 86_400_000) txt = `${Math.round(diffMs / 86_400_000)}d`;
          else if (diffMs < 365 * 86_400_000) txt = `${Math.round(diffMs / (30 * 86_400_000))}mo`;
          else txt = `${Math.round(diffMs / (365 * 86_400_000))}y`;
          const overdue = r.remind_at && r.status === "open" && new Date(r.remind_at) < new Date();
          const tone = done
            ? "bg-emerald-50 text-emerald-700 border-emerald-300"
            : overdue
              ? "bg-rose-500 text-white border-rose-600"
              : "bg-slate-50 text-slate-600 border-slate-300";
          return (
            <span
              className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 border text-[9px] font-semibold uppercase tracking-widest shrink-0 mt-0.5 ${tone}`}
              title={overdue ? `Overdue · created ${new Date(created).toLocaleDateString()}` : `Created ${new Date(created).toLocaleDateString()}`}
              data-testid={`reminder-age-${r.id}`}
            >
              {txt}
            </span>
          );
        })()}
      </div>

      {/* Row 2 — Note (if any) */}
      {r.note && (
        <div className="text-[11px] text-muted-foreground mt-1 ml-8 line-clamp-2 leading-snug">{r.note}</div>
      )}
      {r.status === "done" && r.response_note && (
        <div
          className="mt-1.5 ml-8 px-2 py-1 bg-emerald-50 dark:bg-emerald-950/30 border-l-2 border-l-emerald-400 text-[10px] text-emerald-900 dark:text-emerald-100 leading-snug"
          data-testid={`row-response-${r.id}`}
          title={r.response_note}
        >
          <div className="flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300 mb-0.5">
            <Check className="w-2.5 h-2.5" /> Response
            {r.closed_by_name && <span className="font-normal normal-case tracking-normal text-emerald-700/70 dark:text-emerald-300/70">· by {r.closed_by_name}</span>}
            {r.closed_at && <span className="font-normal normal-case tracking-normal text-emerald-700/70 dark:text-emerald-300/70">· {fmtRelative(r.closed_at)}</span>}
          </div>
          <div className="line-clamp-3 whitespace-pre-wrap break-words">{r.response_note}</div>
        </div>
      )}

      {/* Row 3 — Meta chips (priority · due date · privacy · recurrence · linked · delegate · creator) */}
      <div className="text-[10px] mt-1.5 ml-8 flex items-center gap-1.5 flex-wrap">
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 border ${priChip}`}
               title={`Priority: ${r.priority || "normal"}`}>
          {r.priority || "normal"}
        </span>
        {r.remind_at && (
          <span
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 border ${isOverdue ? "bg-rose-50 text-rose-700 border-rose-300 font-semibold" : "bg-muted/40 text-muted-foreground border-border"}`}
            title={new Date(r.remind_at).toLocaleString()}
            data-testid={`reminder-due-${r.id}`}
          >
            <Clock className="w-2.5 h-2.5" />
            <span>{dueAbsolute}</span>
            <span className="opacity-70">· {dueRelative}</span>
          </span>
        )}
        {isPrivate && !done && (
          <span
            className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide bg-amber-500 text-white border border-amber-600"
            title="Only visible to you"
            data-testid={`reminder-private-${r.id}`}
          >
            <Lock className="w-2.5 h-2.5" /> Only me
          </span>
        )}
        {r.recurrence && (
          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 border bg-muted/40 text-muted-foreground border-border" title={`Repeats ${r.recurrence}`}>
            <Repeat className="w-2.5 h-2.5" /> {r.recurrence}
          </span>
        )}
        {isSnoozed && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-300" data-testid={`reminder-snoozed-${r.id}`}>
            <MoonStar className="w-2.5 h-2.5" />
            until {fmtRelative(r.snooze_until)}
          </span>
        )}
        {r.linked_entity && r.linked_entity.href && (
          <Link
            to={r.linked_entity.href}
            onClick={onCloseFloating}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-primary/10 text-primary border border-primary/30 hover:bg-primary/20 transition max-w-[160px]"
            title={`${r.linked_entity.kind}: ${r.linked_entity.name}`}
            data-testid={`reminder-linked-${r.id}`}
          >
            <Link2 className="w-2.5 h-2.5 shrink-0" />
            <span className="truncate">{r.linked_entity.name}</span>
          </Link>
        )}
        {r.linked_entity && !r.linked_entity.href && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-muted/60 text-muted-foreground border border-border max-w-[160px]">
            <Link2 className="w-2.5 h-2.5 shrink-0" />
            <span className="truncate">{r.linked_entity.name}</span>
          </span>
        )}
        {r.delegated_to && (
          <span
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 border ${isDelegatee ? "bg-emerald-50 text-emerald-700 border-emerald-300" : "bg-muted/60 text-muted-foreground border-border"}`}
            title={isDelegatee ? "Assigned to you" : `Delegated to ${r.delegated_to_name || "teammate"}`}
            data-testid={`reminder-delegatee-${r.id}`}
          >
            <Initials name={r.delegated_to_name} avatar={r.delegated_to_avatar} size={4} />
            <span className="truncate max-w-[80px]">
              {isDelegatee ? "you" : (r.delegated_to_name || "teammate")}
            </span>
          </span>
        )}
        {showCreator && (
          <span
            className="inline-flex items-center gap-1 px-1.5 py-0.5 border bg-muted/40 text-muted-foreground border-border"
            title={`Created by ${r.owner_name || "teammate"}`}
            data-testid={`reminder-creator-${r.id}`}
          >
            <Initials name={r.owner_name} avatar={r.owner_avatar} size={4} />
            <span className="truncate max-w-[80px]">by {r.owner_name || "teammate"}</span>
          </span>
        )}
      </div>

      {/* Row 4 — Action bar (always visible on mobile, hover on desktop) */}
      <div
        className={`ml-8 mt-2 flex items-center gap-1 flex-wrap md:transition md:opacity-0 md:group-hover:opacity-100 ${done ? "md:opacity-100" : ""}`}
        data-testid={`reminder-actions-${r.id}`}
      >
        {!done && (
          <>
            <button
              type="button"
              onClick={onEdit}
              className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest font-semibold border border-border text-muted-foreground hover:text-primary hover:border-primary"
              data-testid={`reminder-edit-${r.id}`}
            >
              <Pencil className="w-3 h-3" /> Edit
            </button>
            {!isSnoozed && (
              <>
                <button
                  type="button"
                  onClick={() => onSnooze(60)}
                  className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest font-semibold border border-border text-muted-foreground hover:text-foreground hover:border-foreground"
                  title="Snooze 1 hour"
                  data-testid={`reminder-snooze-1h-${r.id}`}
                >
                  <MoonStar className="w-3 h-3" /> 1h
                </button>
                <button
                  type="button"
                  onClick={() => onSnooze(60 * 24)}
                  className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest font-semibold border border-border text-muted-foreground hover:text-foreground hover:border-foreground"
                  title="Snooze 1 day"
                  data-testid={`reminder-snooze-1d-${r.id}`}
                >
                  <MoonStar className="w-3 h-3" /> 1d
                </button>
                <button
                  type="button"
                  onClick={() => onSnooze(60 * 24 * 7)}
                  className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest font-semibold border border-border text-muted-foreground hover:text-foreground hover:border-foreground"
                  title="Snooze 1 week"
                  data-testid={`reminder-snooze-1w-${r.id}`}
                >
                  <MoonStar className="w-3 h-3" /> 1w
                </button>
              </>
            )}
            {isSnoozed && (
              <button
                type="button"
                onClick={onUnsnooze}
                className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest font-semibold border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
                data-testid={`reminder-unsnooze-${r.id}`}
              >
                Wake now
              </button>
            )}
          </>
        )}
        <button
          type="button"
          onClick={onDelete}
          className={`ml-auto inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest font-semibold border ${done ? "border-amber-300 text-amber-700 hover:bg-amber-50" : "border-rose-200 text-rose-600 hover:bg-rose-50 hover:border-rose-400"}`}
          title={done ? "Archive (soft delete)" : "Delete"}
          data-testid={`reminder-delete-${r.id}`}
        >
          <Trash2 className="w-3 h-3" /> {done ? "Archive" : "Delete"}
        </button>
      </div>
    </div>
  );
}


// Absolute short-date formatter used by the reminder row's due chip.
// Same day → "Today 4:00 PM", tomorrow → "Tomorrow 9:00 AM", within a
// week → "Wed 3 PM", further out → "21 Sep · 4 PM".
function _fmtAbsShort(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "";
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const tomorrow = new Date(now); tomorrow.setDate(tomorrow.getDate() + 1);
  const isTomorrow = d.toDateString() === tomorrow.toDateString();
  const timePart = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  if (sameDay) return `Today · ${timePart}`;
  if (isTomorrow) return `Tomorrow · ${timePart}`;
  const daysAway = Math.round((d.getTime() - now.getTime()) / 86_400_000);
  if (Math.abs(daysAway) < 7) {
    const wd = d.toLocaleDateString([], { weekday: "short" });
    return `${wd} · ${timePart}`;
  }
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString([], sameYear
    ? { day: "numeric", month: "short" }
    : { day: "numeric", month: "short", year: "2-digit" });
}

// R268~38 · Feb 2026 — Module-level cache so a user opening / closing
// the Edit dialog many times only pays the 4 workspace roundtrips
// once per session (was noticeably slow when Private → Team flipped
// re-triggered a re-render and re-fetch every open).
const _EDIT_DIALOG_OPTS_CACHE = new Map();
async function _loadEditDialogOpts(workspaceId) {
  if (!workspaceId) return { clients: [], brands: [], projects: [], tasks: [] };
  const cached = _EDIT_DIALOG_OPTS_CACHE.get(workspaceId);
  if (cached && Date.now() - cached.ts < 60_000) return cached.data;
  const [cs, bs, ps, ts] = await Promise.allSettled([
    api.get(`/workspaces/${workspaceId}/clients`),
    api.get(`/workspaces/${workspaceId}/brands`),
    api.get(`/workspaces/${workspaceId}/projects`),
    api.get(`/workspaces/${workspaceId}/tasks-search`, { params: { q: "", limit: 500 } }),
  ]);
  const toRows = (r) => {
    if (r.status !== "fulfilled") return [];
    const d = r.value?.data;
    return Array.isArray(d) ? d : (d?.items || d?.rows || []);
  };
  const data = {
    clients: toRows(cs).map((r) => ({ id: r.id, label: r.name || r.title || r.id })),
    brands: toRows(bs).map((r) => ({ id: r.id, label: r.name || r.id, client_id: r.client_id || null })),
    projects: toRows(ps).map((r) => ({ id: r.id, label: r.name || r.id, sublabel: r.client_name, client_id: r.client_id || null, brand_id: r.brand_id || null })),
    tasks: toRows(ts).map((r) => ({ id: r.id, label: r.title || r.id, sublabel: r.project_name, project_id: r.project_id || null, client_id: r.client_id || null, brand_id: r.brand_id || null })),
  };
  _EDIT_DIALOG_OPTS_CACHE.set(workspaceId, { ts: Date.now(), data });
  return data;
}

export function EditReminderDialog({ reminder, members, currentUserId, workspaceId, onClose, onSaved }) {
  const [title, setTitle] = useState(reminder.title || "");
  const [note, setNote] = useState(reminder.note || "");
  const [priority, setPriority] = useState(reminder.priority || "normal");
  const [recurrence, setRecurrence] = useState(reminder.recurrence || "");
  const [remindAt, setRemindAt] = useState(reminder.remind_at ? toLocal(reminder.remind_at) : "");
  const [delegatedTo, setDelegatedTo] = useState(reminder.delegated_to || "");
  const [clientId, setClientId] = useState(reminder.client_id || "");
  const [brandId, setBrandId] = useState(reminder.brand_id || "");
  const [projectId, setProjectId] = useState(reminder.project_id || "");
  const [taskId, setTaskId] = useState(reminder.task_id || "");
  const [isPrivate, setIsPrivate] = useState(!!reminder.is_private);
  // Options for pickers
  const [clientOpts, setClientOpts] = useState([]);
  const [brandOpts, setBrandOpts] = useState([]);
  const [projectOpts, setProjectOpts] = useState([]);
  const [taskOpts, setTaskOpts] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    // R268~38 · Fast path — read from the 60s module cache so repeat
    // opens are instant. Cold-load still runs 4 parallel requests.
    _loadEditDialogOpts(workspaceId).then((d) => {
      if (cancelled) return;
      setClientOpts(d.clients);
      setBrandOpts(d.brands);
      setProjectOpts(d.projects);
      setTaskOpts(d.tasks);
    });
    return () => { cancelled = true; };
  }, [workspaceId]);

  // Cascade — same rules as the Quick Add panel. Selecting a client
  // narrows brands / projects / tasks. Changing a parent to something
  // incompatible with the current child auto-clears the child so we
  // never persist a mismatched pair. Strict — once a parent is set,
  // only children explicitly attached to it appear.
  const brandChoices = useMemo(() => {
    if (!clientId) return brandOpts;
    return brandOpts.filter((b) => b.client_id === clientId);
  }, [brandOpts, clientId]);
  const projectChoices = useMemo(() => {
    return projectOpts.filter((p) => {
      if (clientId && p.client_id !== clientId) return false;
      if (brandId && p.brand_id !== brandId) return false;
      return true;
    });
  }, [projectOpts, clientId, brandId]);
  const taskChoices = useMemo(() => {
    return taskOpts.filter((t) => {
      if (projectId) return t.project_id === projectId;
      if (clientId && t.client_id !== clientId) return false;
      if (brandId && t.brand_id !== brandId) return false;
      return true;
    });
  }, [taskOpts, projectId, clientId, brandId]);
  useEffect(() => {
    if (brandId && brandOpts.length && !brandChoices.some((b) => b.id === brandId)) setBrandId("");
  }, [brandId, brandChoices, brandOpts.length]);
  useEffect(() => {
    if (projectId && projectOpts.length && !projectChoices.some((p) => p.id === projectId)) setProjectId("");
  }, [projectId, projectChoices, projectOpts.length]);
  useEffect(() => {
    if (taskId && taskOpts.length && !taskChoices.some((t) => t.id === taskId)) setTaskId("");
  }, [taskId, taskChoices, taskOpts.length]);

  const save = async () => {
    if (!title.trim()) { toast.error("Title required"); return; }
    setSaving(true);
    try {
      // Every reminder field is now editable. We send the full snapshot
      // so users can also CLEAR an existing link/delegatee (empty string).
      const payload = {
        title: title.trim(),
        note: note.trim() || null,
        priority,
        recurrence: recurrence || "none",
        remind_at: remindAt ? new Date(remindAt).toISOString() : null,
        delegated_to: delegatedTo || null,
        client_id: clientId || null,
        brand_id: brandId || null,
        project_id: projectId || null,
        task_id: taskId || null,
        is_private: !!isPrivate,
      };
      await api.patch(`/reminders/${reminder.id}`, payload);
      toast.success("Saved");
      onSaved();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to save"));
    } finally {
      setSaving(false);
    }
  };

  // R156 · Feb 2026 — Portal the modal to `document.body` so it escapes
  // the RemindersPanel Rnd container (which creates a new stacking
  // context via transform and clips z-[1100] children).
  return createPortal(
    <div
      className="fixed inset-0 z-[1200] bg-black/60 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="reminder-edit-dialog"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl bg-background border border-border shadow-2xl flex flex-col my-auto"
        style={{ maxHeight: "calc(100vh - 2rem)" }}
      >
        <div className="px-4 py-3 border-b border-border flex items-center justify-between bg-muted/40 shrink-0">
          <div>
            <h3 className="text-sm font-semibold">Edit reminder</h3>
            {reminder.owner_name && (
              <div className="text-[10px] text-muted-foreground mt-0.5" data-testid="reminder-edit-creator">
                Created by <span className="font-medium">{reminder.owner_name}</span>
                {reminder.created_at && <> · {fmtRelative(reminder.created_at)}</>}
              </div>
            )}
          </div>
          <button type="button" onClick={onClose} className="p-1 text-muted-foreground hover:text-foreground" data-testid="reminder-edit-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">
          <div>
            <label className="text-[10px] uppercase text-muted-foreground font-medium">Title</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full text-sm px-2 py-1.5 mt-0.5 bg-background border border-border focus:outline-none focus:border-primary"
              data-testid="reminder-edit-title"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase text-muted-foreground font-medium">Note</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              className="w-full text-xs px-2 py-1.5 mt-0.5 bg-background border border-border focus:outline-none focus:border-primary resize-y"
              data-testid="reminder-edit-note"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase text-muted-foreground font-medium">Remind at</label>
              <input
                type="datetime-local"
                value={remindAt}
                onChange={(e) => setRemindAt(e.target.value)}
                className="w-full text-xs px-2 py-1.5 mt-0.5 bg-background border border-border"
                data-testid="reminder-edit-remind-at"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase text-muted-foreground font-medium">Priority</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="w-full text-xs px-2 py-1.5 mt-0.5 bg-background border border-border"
                data-testid="reminder-edit-priority"
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase text-muted-foreground font-medium">Repeat</label>
              <select
                value={recurrence}
                onChange={(e) => setRecurrence(e.target.value)}
                className="w-full text-xs px-2 py-1.5 mt-0.5 bg-background border border-border"
                data-testid="reminder-edit-recurrence"
              >
                <option value="">Once</option>
                <optgroup label="Simple">
                  <option value="daily">Every day</option>
                  <option value="weekdays">Every weekday (Mon–Fri)</option>
                  <option value="weekly">Every week</option>
                  <option value="biweekly">Every 2 weeks</option>
                  <option value="monthly">Every month</option>
                  <option value="quarterly">Every 3 months</option>
                  <option value="yearly">Every year</option>
                </optgroup>
                <optgroup label="Specific weekday">
                  <option value="weekly_mon">Every Monday</option>
                  <option value="weekly_tue">Every Tuesday</option>
                  <option value="weekly_wed">Every Wednesday</option>
                  <option value="weekly_thu">Every Thursday</option>
                  <option value="weekly_fri">Every Friday</option>
                  <option value="weekly_sat">Every Saturday</option>
                  <option value="weekly_sun">Every Sunday</option>
                </optgroup>
                <optgroup label="Monthly patterns">
                  <option value="monthly_first">First of the month</option>
                  <option value="monthly_last">Last of the month</option>
                  <option value="monthly_15">Mid-month (15th)</option>
                </optgroup>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase text-muted-foreground font-medium">Privacy</label>
              <button
                type="button"
                onClick={() => setIsPrivate((v) => !v)}
                className={`w-full text-xs px-2 py-1.5 mt-0.5 border inline-flex items-center gap-1.5 justify-center font-medium ${isPrivate ? "bg-amber-500 text-white border-amber-600" : "bg-background border-border hover:border-amber-400"}`}
                data-testid="reminder-edit-private"
              >
                <Lock className="w-3 h-3" /> {isPrivate ? "Only me" : "Team-visible"}
              </button>
            </div>
            <div className="col-span-2">
              <label className="text-[10px] uppercase text-muted-foreground font-medium">Delegate to</label>
              <ReminderEntityPicker
                value={delegatedTo}
                options={members
                  .filter((m) => (m.user_id || m.id) !== currentUserId)
                  .map((m) => ({
                    id: m.user_id || m.id,
                    label: (m.user?.name || m.name || m.user?.email || m.email || "").trim(),
                    sublabel: (m.user?.email || m.email) && (m.user?.name || m.name) ? (m.user?.email || m.email) : null,
                  }))
                  .filter((m) => m.label)}
                onChange={setDelegatedTo}
                placeholder="— Nobody —"
                testid="reminder-edit-delegate"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase text-muted-foreground font-medium">Client</label>
              <ReminderEntityPicker
                value={clientId}
                options={clientOpts}
                onChange={setClientId}
                placeholder="— Client —"
                testid="reminder-edit-client"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase text-muted-foreground font-medium">
                Brand {clientId && <span className="text-[9px] text-muted-foreground/80 normal-case">· filtered</span>}
              </label>
              <ReminderEntityPicker
                value={brandId}
                options={brandChoices}
                onChange={setBrandId}
                placeholder={clientId && brandChoices.length === 0 ? "No brands for this client" : "— Brand —"}
                testid="reminder-edit-brand"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase text-muted-foreground font-medium">
                Project {(clientId || brandId) && <span className="text-[9px] text-muted-foreground/80 normal-case">· filtered</span>}
              </label>
              <ReminderEntityPicker
                value={projectId}
                options={projectChoices}
                onChange={setProjectId}
                placeholder={(clientId || brandId) && projectChoices.length === 0 ? "No projects match" : "— Project —"}
                testid="reminder-edit-project"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase text-muted-foreground font-medium">
                Task {(projectId || clientId || brandId) && <span className="text-[9px] text-muted-foreground/80 normal-case">· filtered</span>}
              </label>
              <ReminderEntityPicker
                value={taskId}
                options={taskChoices}
                onChange={setTaskId}
                placeholder={(projectId || clientId || brandId) && taskChoices.length === 0 ? "No tasks match" : "— Task —"}
                testid="reminder-edit-task"
              />
            </div>
          </div>
        </div>
        <div className="px-4 py-3 border-t border-border flex items-center justify-end gap-2 bg-muted/20 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="text-xs px-3 py-1.5 border border-border hover:bg-muted"
            data-testid="reminder-edit-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="text-xs px-3 py-1.5 bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50 font-medium"
            data-testid="reminder-edit-save"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// Convert ISO to "YYYY-MM-DDTHH:mm" for datetime-local input
function toLocal(iso) {
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
}


// -----------------------------------------------------------------------------
// Feb 2026 · Voice dictation button — uses the browser's Web Speech API
// (Chrome, Safari, Edge). Tap once to start listening; tap again to stop.
// Interim transcript is not applied — only the final segment is written to
// the input via the `onParsed` callback, which receives `{title, remindAt}`
// after a lightweight natural-language pass (see `parseReminderPhrase`).
// Falls back to a disabled state with a tooltip when the browser doesn't
// support recognition.
function VoiceDictateButton({ onParsed, testid = "reminders-voice-dictate" }) {
  const [supported, setSupported] = useState(false);
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);

  useEffect(() => {
    const Ctor = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
    setSupported(!!Ctor);
    if (!Ctor) return;
    const rec = new Ctor();
    rec.continuous = false;
    rec.interimResults = false;
    rec.lang = navigator.language || "en-US";
    rec.onresult = (ev) => {
      let final = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const res = ev.results[i];
        if (res.isFinal) final += res[0].transcript;
      }
      if (final) {
        const parsed = parseReminderPhrase(final);
        onParsed(parsed);
      }
    };
    rec.onend = () => setListening(false);
    rec.onerror = (e) => {
      setListening(false);
      const msg = e?.error === "not-allowed"
        ? "Microphone permission denied"
        : e?.error === "no-speech"
          ? "Didn't catch that — try again"
          : `Voice error: ${e?.error || "unknown"}`;
      try { toast.error(msg); } catch { /* toast may be unavailable in some paths */ }
    };
    recRef.current = rec;
    return () => { try { rec.abort(); } catch { /* noop */ } };
  }, [onParsed]);

  const toggle = () => {
    if (!recRef.current) return;
    if (listening) { try { recRef.current.stop(); } catch { /* noop */ } setListening(false); return; }
    try { recRef.current.start(); setListening(true); }
    catch { setListening(false); }
  };

  if (!supported) {
    return (
      <button
        type="button"
        disabled
        title="Voice dictation isn't supported in this browser. Try Chrome or Safari on iOS/Android."
        className="text-xs px-2.5 py-2 border border-border bg-muted/30 text-muted-foreground/60 cursor-not-allowed"
        data-testid={`${testid}-unsupported`}
      >
        <Mic className="w-3.5 h-3.5" />
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={toggle}
      title={listening ? "Tap to stop dictation" : "Dictate reminder from your voice"}
      className={`relative text-xs px-2.5 py-2 border transition-colors ${listening
        ? "bg-rose-500 text-white border-rose-600 animate-pulse"
        : "bg-background border-border text-muted-foreground hover:text-primary hover:border-primary"}`}
      data-testid={testid}
      aria-pressed={listening}
    >
      <Mic className="w-3.5 h-3.5" />
      {listening && (
        <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-rose-400 animate-ping" />
      )}
    </button>
  );
}



// -----------------------------------------------------------------------------
// Feb 2026 · parseReminderPhrase(raw)
// Best-effort natural language parser for voice-dictated reminders.
// Understands:
//   • "remind me to <title>" / "remind me <title>" — stripped from title
//   • Weekday/relative dates: today, tomorrow, tonight, next monday…
//   • Explicit dates: "on 12 April", "12th April", "12 apr"
//   • Times: "at 4pm", "4pm", "4:30 pm", "16:00", "at 4"  (default: 9AM if
//     a date is spoken without a time; if only a time is spoken it's today
//     — or tomorrow when the time is already past)
//   • "in 2 hours", "in 30 minutes", "in 3 days"
// Returns { title: string, remindAt: string ISO | null }. Anything the
// parser can't match falls through to the title so the user still gets a
// clean transcription.
function parseReminderPhrase(raw) {
  let text = String(raw || "").trim();
  if (!text) return { title: "", remindAt: null };
  // Remove leading polite/command noise
  text = text.replace(/^(hey |ok |okay |please )+/i, "").trim();
  text = text.replace(/^remind me to /i, "").replace(/^remind me /i, "").trim();

  const now = new Date();
  let due = null;

  // --- "in N (minute|hour|day|week)s?" ---
  const rel = text.match(/\bin\s+(a|an|\d+)\s+(minute|min|hour|hr|day|week)s?\b/i);
  if (rel) {
    const n = rel[1].match(/\d+/) ? parseInt(rel[1], 10) : 1;
    const unit = rel[2].toLowerCase();
    due = new Date(now);
    if (/min/.test(unit)) due.setMinutes(due.getMinutes() + n);
    else if (/hour|hr/.test(unit)) due.setHours(due.getHours() + n);
    else if (/day/.test(unit)) due.setDate(due.getDate() + n);
    else if (/week/.test(unit)) due.setDate(due.getDate() + 7 * n);
    text = text.replace(rel[0], "").trim();
  }

  // --- Weekday: "next monday", "on friday", "this thursday" ---
  const wdays = ["sunday","monday","tuesday","wednesday","thursday","friday","saturday"];
  const wd = text.match(/\b(?:on\s+)?(?:this\s+|next\s+|upcoming\s+)?(sunday|monday|tuesday|wednesday|thursday|friday|saturday)\b/i);
  if (!due && wd) {
    const target = wdays.indexOf(wd[1].toLowerCase());
    const isNext = /next|upcoming/i.test(wd[0]);
    const cur = now.getDay();
    let delta = (target - cur + 7) % 7;
    if (delta === 0 || isNext) delta = delta === 0 ? 7 : delta;
    due = new Date(now); due.setDate(due.getDate() + delta); due.setHours(9, 0, 0, 0);
    text = text.replace(wd[0], "").trim();
  }

  // --- today | tomorrow | tonight ---
  if (!due) {
    if (/\btoday\b/i.test(text)) {
      due = new Date(now); due.setHours(Math.max(now.getHours(), 9), 0, 0, 0);
      text = text.replace(/\btoday\b/i, "").trim();
    } else if (/\btomorrow\b/i.test(text)) {
      due = new Date(now); due.setDate(due.getDate() + 1); due.setHours(9, 0, 0, 0);
      text = text.replace(/\btomorrow\b/i, "").trim();
    } else if (/\btonight\b/i.test(text)) {
      due = new Date(now); due.setHours(20, 0, 0, 0);
      text = text.replace(/\btonight\b/i, "").trim();
    }
  }

  // --- "at 4pm", "at 4:30 pm", "at 16:00", plain "4pm" ---
  const timeRe = /\b(?:at\s+)?(\d{1,2})(?::(\d{2}))?\s*(am|pm|a\.m\.|p\.m\.)?\b/i;
  const tm = text.match(timeRe);
  if (tm) {
    let h = parseInt(tm[1], 10);
    const min = tm[2] ? parseInt(tm[2], 10) : 0;
    const suffix = (tm[3] || "").toLowerCase().replace(/\./g, "");
    // Only take the match as a time if it has am/pm OR has a colon minutes portion OR the number is > 23.
    // Otherwise a bare "4" is probably part of the title.
    const looksLikeTime = suffix || tm[2] || h > 23;
    if (looksLikeTime && h <= 23) {
      if (suffix === "pm" && h < 12) h += 12;
      if (suffix === "am" && h === 12) h = 0;
      if (!due) {
        due = new Date(now); due.setHours(h, min, 0, 0);
        // If the time is already past, roll to tomorrow
        if (due.getTime() < now.getTime() - 60_000) due.setDate(due.getDate() + 1);
      } else {
        due.setHours(h, min, 0, 0);
      }
      text = text.replace(tm[0], "").trim();
    }
  }

  // Clean fillers left over ("on", "at", commas, double spaces)
  text = text
    .replace(/\b(?:on|at)\b/gi, " ")
    .replace(/[,\s]{2,}/g, " ")
    .replace(/^[\s,.-]+|[\s,.-]+$/g, "")
    .replace(/^to\s+/i, "")
    .replace(/\.$/, "")
    .trim();

  // Capitalise first letter
  if (text) text = text.charAt(0).toUpperCase() + text.slice(1);

  return { title: text, remindAt: due ? due.toISOString() : null };
}

